import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Upload, 
  Download, 
  Search, 
  AlertCircle, 
  CheckCircle2 as CheckCircle, // Force rebuild by aliasing 
  AlertTriangle,
  Copy,
  FileText,
  X,
  ArrowLeft,
  FileSpreadsheet,
  FileImage,
  ExternalLink
} from 'lucide-react'
import jsPDF from 'jspdf'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { Link } from 'react-router-dom'
import { CompanyConfigPanel } from '@/components/CompanyConfigPanel'
import SanityTest from '@/components/SanityTest'
import { FieldMappingModal, FieldMapping, CSVDialect } from '@/components/FieldMappingModal'
import { MappingStatusCard } from '@/components/MappingStatusCard'
import { useFieldMappings } from '@/hooks/useFieldMappings'
import { 
  sniffCSVDialect, 
  parseCSVForMapping, 
  suggestFieldMapping, 
  validateFieldMapping,
  SKATTEKONTO_SCHEMA 
} from '@/components/FieldMappingUtils'

// Sanitera filnamn för Supabase Storage-nycklar (ASCII only)
function sanitizeFilename(original: string): string {
  // 1) Normalisera Unicode till NFKD och ta bort diakritiska tecken
  const normalized = original.normalize('NFKD').replace(/\p{Diacritic}/gu, '');
  // 2) Ersätt mellanslag med underscore
  const noSpaces = normalized.replace(/\s+/g, '_');
  // 3) Ta bort alla tecken som inte är a-z, A-Z, 0-9, punkt, minus eller underscore
  const asciiSafe = noSpaces.replace(/[^a-zA-Z0-9._-]/g, '_');
  // 4) Komprimera multipla underscores och trimma
  const collapsed = asciiSafe.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  // 5) Begränsa längd (för säkerhets skull)
  return collapsed.slice(0, 120) || 'file';
}

interface CompanyConfig {
  companyType: 'ab' | 'enskild' | 'handelsbolag' | 'kommanditbolag' | 'ekonomisk_forening';
  annualTurnover: 'under_1m' | '1m_to_40m' | 'over_40m';
  taxReportingPeriod: 'monthly' | 'quarterly' | 'annually';
  vatReportingPeriod: 'monthly' | 'quarterly' | 'annually';
  toleranceDays: number; // 0-7 dagar tolerans efter förfallodatum
}

interface Finding {
  id: string;
  period: string;
  daysLate: number;
  amount: number;
  message: string;
  severity: 'low' | 'medium' | 'high';
  referenceId?: string;
  findingType: 'late_payment' | 'interest_charge' | 'amount_deviation' | 'period_mismatch' | 'compliance_violation' | 'irregular_pattern';
  riskScore: number; // 0-100
  recommendation?: string;
  details?: {
    expectedAmount?: number;
    actualAmount?: number;
    expectedDate?: string;
    actualDate?: string;
    complianceRule?: string;
    interestRate?: number;
  };
}

export function TestlabTaxAccountPage() {
  // Test sync after repository rename to "auditai" - push test 2
  const { t } = useTranslation('common')
  const { toast } = useToast()
  const [findings, setFindings] = useState<Finding[]>([])
  const [loading, setLoading] = useState(false)
  const [showJsonModal, setShowJsonModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<Array<{id: string, name: string, size: number, uploadedAt: Date}>>([])
  const [uploading, setUploading] = useState(false)
  const [companyConfig, setCompanyConfig] = useState<CompanyConfig>({
    companyType: 'ab',
    annualTurnover: '1m_to_40m',
    taxReportingPeriod: 'monthly',
    vatReportingPeriod: 'quarterly', // Kvartalsvis är vanligare för små bolag
    toleranceDays: 3 // Standard 3 dagars tolerans
  })
  const [showConfig, setShowConfig] = useState(true) // Visa konfiguration först
  
  // Field mapping state
  const [showFieldMapping, setShowFieldMapping] = useState(false)
  const [pendingFile, setPendingFile] = useState<{
    file: File;
    content: string;
    headers: string[];
    preview: string[][];
    dialect: CSVDialect;
  } | null>(null)
  const [fieldMappings, setFieldMappings] = useState<Record<string, FieldMapping[]>>({}) // Store mappings per file
  
  const { saveMapping, getMapping } = useFieldMappings()

  // Ladda upp fil och initiera fältmappning
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    
    try {
      for (const file of Array.from(files)) {
        if (!file.name.toLowerCase().endsWith('.csv')) {
          toast({
            title: "Felaktigt filformat", 
            description: `${file.name} är inte en CSV-fil`,
            variant: "destructive"
          })
          continue
        }

        // Read file content for field mapping
        const fileContent = await file.text()
        const dialect = sniffCSVDialect(fileContent)
        const { headers, preview } = parseCSVForMapping(fileContent, dialect)
        
        if (headers.length === 0) {
          toast({
            title: "Tom fil",
            description: `${file.name} verkar vara tom eller felformaterad`,
            variant: "destructive"
          })
          continue
        }

        // Check for existing mapping
        const customerId = 'default' // For now, use default - could be configurable
        const existingMapping = await getMapping(customerId, 'skattekonto')
        
        if (existingMapping) {
          // Use existing mapping and upload file directly
          await uploadFileWithMapping(file, existingMapping.mapping, existingMapping.dialect)
        } else {
          // Show field mapping modal
          setPendingFile({
            file,
            content: fileContent,
            headers,
            preview,
            dialect
          })
          setShowFieldMapping(true)
          break // Handle one file at a time when mapping is needed
        }
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast({
        title: "Uppladdningsfel", 
        description: "Ett fel uppstod vid filbehandling",
        variant: "destructive"
      })
    } finally {
      setUploading(false)
    }
  }

  // Upload file with confirmed mapping
  const uploadFileWithMapping = async (
    file: File, 
    mapping: Record<string, string>, 
    dialect: CSVDialect
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        toast({
          title: "Autentiseringsfel",
          description: "Du måste vara inloggad",
          variant: "destructive"
        })
        return
      }

      // Sanitera filnamnet ordentligt (ASCII-only, inga diakritiska tecken)
      const sanitizedName = sanitizeFilename(file.name)
      const fileName = `${user.id}/${Date.now()}-${sanitizedName}`
      
      console.log('Uploading file:', { 
        original: file.name, 
        sanitized: sanitizedName, 
        fullPath: fileName 
      })
      
      // Ladda upp till Supabase Storage
      let uploadRes = await supabase.storage
        .from('testlab-files')
        .upload(fileName, file)

      if (uploadRes.error && uploadRes.error.message?.toLowerCase().includes('invalid key')) {
        // Fallback: använd super-säkert filnamn
        const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}.csv`
        const safePath = `${user.id}/${safeName}`
        console.warn('Retrying upload with safe filename:', { safePath })
        uploadRes = await supabase.storage
          .from('testlab-files')
          .upload(safePath, file)

        if (!uploadRes.error) {
          (uploadRes as any).data = { path: safePath }
        }
      }

      const { data, error } = uploadRes

      if (error) {
        console.error('Upload error:', error)
        toast({
          title: "Uppladdningsfel",
          description: `Kunde inte ladda upp ${file.name}: ${error.message}`,
          variant: "destructive"
        })
        return
      }

      // Store mapping for this file
      const fileId = (data as any).path
      setFieldMappings(prev => ({
        ...prev,
        [fileId]: Object.entries(mapping).map(([originalName, mappedName]) => ({
          originalName,
          mappedName,
          confidence: 1.0,
          isRequired: SKATTEKONTO_SCHEMA.required_min.includes(mappedName)
        }))
      }))

      // Lägg till i listan över uppladdade filer
      const fileInfo = {
        id: fileId,
        name: file.name,
        size: file.size,
        uploadedAt: new Date()
      }
      
      setUploadedFiles(prev => [...prev, fileInfo])
      
      toast({
        title: "Fil uppladdad",
        description: `${file.name} har laddats upp med fältmappning`
      })
    } catch (error) {
      console.error('Upload error:', error)
      toast({
        title: "Uppladdningsfel", 
        description: "Ett fel uppstod vid uppladdning",
        variant: "destructive"
      })
    }
  }

  // Ta bort fil från storage och lista
  const removeFile = async (fileId: string, fileName: string) => {
    try {
      const { error } = await supabase.storage
        .from('testlab-files')
        .remove([fileId])

      if (error) {
        toast({
          title: "Fel vid borttagning",
          description: `Kunde inte ta bort ${fileName}`,
          variant: "destructive"
        })
        return
      }

      setUploadedFiles(prev => prev.filter(f => f.id !== fileId))
      toast({
        title: "Fil borttagen",
        description: `${fileName} har tagits bort`
      })
    } catch (error) {
      console.error('Remove error:', error)
    }
  }

  // Handle field mapping confirmation - wrapper for new modal format
  const handleNewFieldMappingConfirm = async (mapping: Record<string, string>) => {
    // Convert new format to old format for compatibility
    const mappings: FieldMapping[] = Object.entries(mapping).map(([target, source]) => ({
      originalName: source,
      mappedName: target,
      confidence: 1.0,
      isRequired: ['Datum', 'Händelse', 'Belopp'].includes(target)
    }));
    
    const dialect: CSVDialect = {
      delimiter: ';',
      encoding: 'utf-8',
      hasHeader: true
    };
    
    await handleFieldMappingConfirm(mappings, dialect, 'test-customer');
  };

  // Handle field mapping confirmation - original function
  const handleFieldMappingConfirm = async (
    mappings: FieldMapping[], 
    dialect: CSVDialect, 
    customerId: string,
    templateName?: string
  ) => {
    if (!pendingFile) return

    // Save mapping for future use
    const mappingRecord = Object.fromEntries(
      mappings
        .filter(m => m.mappedName !== 'unmapped')
        .map(m => [m.originalName, m.mappedName])
    )

    await saveMapping(customerId, 'skattekonto', mappingRecord, dialect, templateName)

    // Upload file with confirmed mapping
    await uploadFileWithMapping(pendingFile.file, mappingRecord, dialect)
    
    // Close modal and clear pending file
    setShowFieldMapping(false)
    setPendingFile(null)
  }

  const handleFieldMappingCancel = () => {
    setShowFieldMapping(false)
    setPendingFile(null)
  }

  // Starta analys av alla uppladdade filer
  const startAnalysis = async () => {
    if (uploadedFiles.length === 0) {
      toast({
        title: "Inga filer",
        description: "Ladda upp minst en CSV-fil innan analys",
        variant: "destructive"
      })
      return
    }

    // Validate that all files have field mappings
    const filesWithoutMappings = uploadedFiles.filter(f => !fieldMappings[f.id])
    if (filesWithoutMappings.length > 0) {
      toast({
        title: "Ofullständig mappning",
        description: "Alla filer måste ha fältmappning innan analys",
        variant: "destructive"
      })
      return
    }

    setLoading(true)
    setError(null)
    
    try {
      // Include field mappings in analysis request
      const filesWithMappings = uploadedFiles.map(f => ({
        fileId: f.id,
        fieldMapping: fieldMappings[f.id]
      }))

      const { data: result, error } = await supabase.functions.invoke('testlab-tax-account-analyze', {
        body: { 
          filesWithMappings,
          analysisType: 'multiple_files_with_mapping',
          companyConfig: companyConfig
        }
      })

      if (error) {
        throw new Error(error.message || 'Analys misslyckades')
      }

      setFindings(result.findings || [])
      
      if (result.findings?.length > 0) {
        toast({
          title: "Analys slutförd",
          description: `${result.findings.length} finding(s) upptäckta från ${uploadedFiles.length} fil(er)`
        })
      } else {
        toast({
          title: "Analys slutförd", 
          description: "Inga avvikelser upptäcktes"
        })
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Okänt fel'
      setError(errorMessage)
      toast({
        title: "Analysfel",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const useDummyData = async () => {
    try {
      const { data: csvContent, error: dummyError } = await supabase.functions.invoke('testlab-tax-account-dummy')
      if (dummyError) throw new Error(dummyError.message || 'Failed to fetch dummy data')
      
      // Skapa temporär fil från dummy data och "ladda upp"
      const dummyFile = new File([csvContent], 'dummy-skattekonto.csv', { type: 'text/csv' })
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      
      const fileName = `${user.id}/${Date.now()}-dummy-skattekonto.csv`
      const { data, error: uploadError } = await supabase.storage
        .from('testlab-files')
        .upload(fileName, dummyFile)

      if (uploadError) throw uploadError

      const fileInfo = {
        id: data.path,
        name: 'dummy-skattekonto.csv',
        size: dummyFile.size,
        uploadedAt: new Date()
      }
      
      setUploadedFiles(prev => [...prev, fileInfo])
      
      toast({
        title: "Dummydata laddad",
        description: "Testfil med skattekonto-data har laddats upp"
      })
    } catch (error) {
      toast({
        title: "Fel",
        description: "Kunde inte ladda dummydata",
        variant: "destructive"
      })
    }
  }

  // Ta bort den gamla analyzeCSV funktionen helt

  const getSeverityColor = (severity: Finding['severity']) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(findings, null, 2))
    toast({
      title: "Kopierat",
      description: "JSON-data kopierad till urklipp"
    })
  }

  const exportToCSV = () => {
    if (findings.length === 0) return

    const headers = ['Period', 'Dagar försenade', 'Belopp (kr)', 'Meddelande', 'Allvarlighetsgrad', 'Typ', 'Riskpoäng']
    const csvContent = [
      headers.join(','),
      ...findings.map(finding => [
        `"${finding.period}"`,
        finding.daysLate,
        finding.amount,
        `"${finding.message}"`,
        finding.severity,
        finding.findingType,
        finding.riskScore
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `skattekonto-analys-${new Date().toISOString().slice(0, 10)}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast({
      title: "CSV exporterad",
      description: "Analysresultat har laddats ner som CSV-fil"
    })
  }

  const exportToJSON = () => {
    if (findings.length === 0) return

    const jsonData = {
      exportDate: new Date().toISOString(),
      totalFindings: findings.length,
      severitySummary: {
        high: findings.filter(f => f.severity === 'high').length,
        medium: findings.filter(f => f.severity === 'medium').length,
        low: findings.filter(f => f.severity === 'low').length
      },
      findings: findings
    }

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `skattekonto-analys-${new Date().toISOString().slice(0, 10)}.json`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast({
      title: "JSON exporterad",
      description: "Analysresultat har laddats ner som JSON-fil"
    })
  }

  const exportToPDF = () => {
    if (findings.length === 0) return

    const pdf = new jsPDF()
    const pageWidth = pdf.internal.pageSize.width
    const margin = 20
    let yPosition = margin

    // Header
    pdf.setFontSize(20)
    pdf.text('Skattekonto Analysrapport', margin, yPosition)
    yPosition += 15

    pdf.setFontSize(12)
    pdf.text(`Exportdatum: ${new Date().toLocaleDateString('sv-SE')}`, margin, yPosition)
    yPosition += 10
    pdf.text(`Antal findings: ${findings.length}`, margin, yPosition)
    yPosition += 15

    // Summary
    const highCount = findings.filter(f => f.severity === 'high').length
    const mediumCount = findings.filter(f => f.severity === 'medium').length
    const lowCount = findings.filter(f => f.severity === 'low').length

    pdf.setFontSize(14)
    pdf.text('Sammanfattning:', margin, yPosition)
    yPosition += 10

    pdf.setFontSize(11)
    pdf.text(`• Hög risk: ${highCount} findings`, margin + 5, yPosition)
    yPosition += 7
    pdf.text(`• Medium risk: ${mediumCount} findings`, margin + 5, yPosition)
    yPosition += 7
    pdf.text(`• Låg risk: ${lowCount} findings`, margin + 5, yPosition)
    yPosition += 15

    // Findings details
    pdf.setFontSize(14)
    pdf.text('Detaljerade findings:', margin, yPosition)
    yPosition += 10

    findings.forEach((finding, index) => {
      if (yPosition > 250) { // New page if needed
        pdf.addPage()
        yPosition = margin
      }

      pdf.setFontSize(11)
      pdf.text(`${index + 1}. ${finding.message}`, margin, yPosition)
      yPosition += 7
      pdf.text(`   Period: ${finding.period} | Dagar: ${finding.daysLate} | Belopp: ${finding.amount.toLocaleString('sv-SE')} kr`, margin, yPosition)
      yPosition += 7
      pdf.text(`   Risk: ${finding.severity} (${finding.riskScore}/100)`, margin, yPosition)
      yPosition += 10
    })

    pdf.save(`skattekonto-analys-${new Date().toISOString().slice(0, 10)}.pdf`)

    toast({
      title: "PDF exporterad",
      description: "Analysrapport har laddats ner som PDF-fil"
    })
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Dev Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <p className="font-medium text-amber-800">{t('testlab.banner')}</p>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Link to="/testlab">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Tillbaka till Testlab
              </Button>
            </Link>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">{t('testlab.tax.title')}</h1>
          <p className="text-lg text-muted-foreground">
            {t('testlab.tax.desc')}
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          Modul 6
        </Badge>
      </div>

      {/* Field Mapping Modal */}
      {pendingFile && (
        <FieldMappingModal
          open={showFieldMapping}
          onOpenChange={setShowFieldMapping}
          csvHeaders={pendingFile.headers}
          csvPreview={pendingFile.preview}
          suggestedMappings={suggestFieldMapping(pendingFile.headers, SKATTEKONTO_SCHEMA)}
          requiredFields={SKATTEKONTO_SCHEMA.required_min}
          optionalFields={SKATTEKONTO_SCHEMA.optional}
          onConfirm={handleNewFieldMappingConfirm}
          onCancel={handleFieldMappingCancel}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Configuration */}
          {showConfig && (
            <CompanyConfigPanel 
              config={companyConfig}
              onConfigChange={setCompanyConfig}
              onContinue={() => setShowConfig(false)}
            />
          )}

          {/* Sanity Test */}
          {!showConfig && (
            <SanityTest />
          )}

          {/* Upload Section */}
          {!showConfig && (
            <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Ladda upp filer
              </CardTitle>
              <CardDescription>
                Ladda upp en eller flera CSV-filer från Skatteverket för analys. Du kan lägga till flera filer och starta analysen när du är redo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Fil-uppladdning */}
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <Upload className="w-8 h-8 mx-auto mb-4 text-muted-foreground" />
                <div className="space-y-2">
                  <p className="text-sm font-medium">Ladda upp CSV-filer</p>
                  <p className="text-xs text-muted-foreground">
                    Välj en eller flera filer från Skatteverket
                  </p>
                </div>
                <div className="mt-4 flex gap-2 justify-center">
                  <Button 
                    onClick={() => document.getElementById('csv-upload')?.click()}
                    disabled={uploading}
                    size="sm"
                  >
                    {uploading ? 'Laddar upp...' : 'Välj filer'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={useDummyData}
                    disabled={uploading}
                    size="sm"
                  >
                    Använd testdata
                  </Button>
                </div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                  disabled={uploading}
                  multiple
                />
              </div>

              {/* Lista över uppladdade filer */}
              {uploadedFiles.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Uppladdade filer ({uploadedFiles.length})</h4>
                  <div className="space-y-2">
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-3 border rounded-md bg-muted/50">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{file.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(file.size / 1024).toFixed(1)} KB • {file.uploadedAt.toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.id, file.name)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Start analys knapp */}
                  <Button 
                    onClick={startAnalysis}
                    disabled={loading || uploadedFiles.length === 0}
                    className="w-full"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Analyserar...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Starta analys av {uploadedFiles.length} fil(er)
                      </>
                    )}
                  </Button>
                </div>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {error}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
          )}

          {/* Results Section */}
          {findings.length > 0 ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Analysresultat ({findings.length} findings)
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={exportToCSV} className="gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportToPDF} className="gap-2">
                      <FileImage className="h-4 w-4" />
                      PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportToJSON} className="gap-2">
                      <Download className="h-4 w-4" />
                      JSON
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('testlab.tax.table.period')}</TableHead>
                      <TableHead>{t('testlab.tax.table.days')}</TableHead>
                      <TableHead>{t('testlab.tax.table.amount')}</TableHead>
                      <TableHead>{t('testlab.tax.table.message')}</TableHead>
                      <TableHead>Allvarlighetsgrad</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {findings.map((finding) => (
                      <TableRow key={finding.id}>
                        <TableCell className="font-medium">{finding.period}</TableCell>
                        <TableCell>{finding.daysLate}</TableCell>
                        <TableCell>{finding.amount.toLocaleString('sv-SE')} kr</TableCell>
                        <TableCell>{finding.message}</TableCell>
                        <TableCell>
                          <Badge className={getSeverityColor(finding.severity)}>
                            {finding.severity}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : !loading && !error && (
            <Card>
              <CardContent className="py-8">
                <div className="text-center space-y-3">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                  <p className="text-lg font-medium">{t('testlab.tax.noFindings')}</p>
                  <p className="text-sm text-muted-foreground">
                    Inga sena betalningar upptäcktes i den analyserade datan.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Promote Tips */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Produktionsintegration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <h4 className="font-medium mb-2">Var används denna modul?</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• Dashboard widgets (risköversikt)</li>
                  <li>• PDF-exportfunktion</li>
                  <li>• Automatiska bakgrundsjobb</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Implementation roadmap</h4>
                <ol className="space-y-1 text-muted-foreground list-decimal list-inside">
                  <li>Testning i Testlab ✓</li>
                  <li>Integration i backend-jobb</li>
                  <li>Spara findings i module_findings</li>
                  <li>Visa i dashboard</li>
                  <li>Inkludera i PDF-export</li>
                </ol>
              </div>

              <div className="pt-4 border-t">
                <Button variant="outline" size="sm" className="w-full gap-2" asChild>
                  <Link to="/dashboard">
                    <ExternalLink className="h-4 w-4" />
                    Visa Dashboard
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">CSV-format</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p className="text-muted-foreground">Nödvändiga kolumner:</p>
              <ul className="space-y-1 text-xs font-mono bg-muted p-2 rounded">
                <li>• Bokföringsdag</li>
                <li>• Text</li>
                <li>• Belopp</li>
                <li>• Saldo</li>
              </ul>
              <p className="text-muted-foreground">Valfria kolumner:</p>
              <ul className="space-y-1 text-xs font-mono bg-muted p-2 rounded">
                <li>• Period</li>
                <li>• Referens</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* JSON Modal */}
      <Dialog open={showJsonModal} onOpenChange={setShowJsonModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Analysresultat (JSON)</DialogTitle>
            <DialogDescription>
              Rå JSON-data från skattekonto-analysen
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={copyToClipboard}>
                Kopiera till urklipp
              </Button>
            </div>
            <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
              {JSON.stringify(findings, null, 2)}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}